import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { locationService } from '../services/locationService';
import { countryService } from '../services/countryService';
import './CountrySelectionPage.css';

const CountrySelectionPage = () => {
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { isLoggedIn, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadCountries();
    checkCountryAndRedirect();
  }, [isLoggedIn, authLoading]);

  const checkCountryAndRedirect = async () => {
    // Wait for auth to finish loading
    if (authLoading) return;

    // Check if both country and city are selected
    const country = await countryService.getSelectedCountry();
    const city = await countryService.getSelectedCity();
    if (country && city) {
      navigate('/', { replace: true });
    }
  };

  const loadCountries = async () => {
    try {
      setLoading(true);
      const countriesData = await locationService.getCountries();
      setCountries(countriesData);
    } catch (error) {
      console.error('Error loading countries:', error);
      setError('Failed to load countries. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const loadCities = async (countryName) => {
    if (!countryName) {
      setCities([]);
      setSelectedCity('');
      return;
    }

    try {
      setLoadingCities(true);
      const citiesData = await locationService.getCitiesByCountry(countryName);
      setCities(citiesData);
      setSelectedCity(''); // Reset city when country changes
    } catch (error) {
      console.error('Error loading cities:', error);
      setError('Failed to load cities. Please try again.');
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setSelectedCity(''); // Reset city when country changes
    setError('');
    if (country) {
      loadCities(country);
    } else {
      setCities([]);
    }
  };

  const handleContinue = async () => {
    if (!selectedCountry) {
      setError('Please select a country');
      return;
    }

    if (!selectedCity) {
      setError('Please select a city');
      return;
    }

    try {
      setError('');
      setLoading(true);
      // Save both country and city
      await countryService.setLocation(selectedCountry, selectedCity);
      console.log('Location saved:', selectedCountry, selectedCity);
      // Small delay to ensure location is saved to localStorage
      await new Promise(resolve => setTimeout(resolve, 200));
      // Redirect to home page (/) which will show filtered products
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error saving location:', error);
      setError('Failed to save location. Please try again.');
      setLoading(false);
    }
  };

  // Show loading while checking auth or if already has country
  if (authLoading || loading) {
    return (
      <div className="country-selection-page">
        <div className="country-selection-loading">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="country-selection-page">
      <div className="country-selection-container">
        <div className="country-selection-header">
          <h1>Welcome!</h1>
          <p>Please select your country and city to see products available in your region</p>
        </div>

        {error && (
          <div className="country-selection-error">
            <span className="error-icon">❌</span>
            {error}
          </div>
        )}

        <div className="country-selection-content">
          <div className="country-select-wrapper">
            <label htmlFor="country-select">Select Your Country</label>
            <select
              id="country-select"
              value={selectedCountry}
              onChange={(e) => handleCountryChange(e.target.value)}
              className="country-select"
            >
              <option value="">-- Select a country --</option>
              {countries.map((country) => (
                <option key={country.code} value={country.name}>
                  {country.name} ({country.currency_symbol} {country.currency})
                </option>
              ))}
            </select>
          </div>

          {selectedCountry && (
            <div className="country-select-wrapper" style={{ marginTop: '20px' }}>
              <label htmlFor="city-select">Select Your City</label>
              {loadingCities ? (
                <div style={{ padding: '10px', textAlign: 'center', color: '#666' }}>
                  Loading cities...
                </div>
              ) : (
                <select
                  id="city-select"
                  value={selectedCity}
                  onChange={(e) => {
                    setSelectedCity(e.target.value);
                    setError('');
                  }}
                  className="country-select"
                >
                  <option value="">-- Select a city --</option>
                  {cities.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>

        <div className="country-selection-footer">
          <button
            onClick={handleContinue}
            disabled={!selectedCountry || !selectedCity || loading}
            className="country-selection-button"
          >
            Continue
          </button>
          <p className="country-selection-login-prompt">
            Already have an account? <a href="/login">Login</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CountrySelectionPage;

